/**
 * Workflow Autosave Service
 * Handles automatic saving and loading of workflow state to/from localStorage
 * Features:
 * - 30-second autosave interval
 * - Debounced saves to prevent excessive writes
 * - Version control with timestamps
 * - Multiple workflow support
 * - Automatic cleanup of old saves
 */

const AUTOSAVE_INTERVAL = 30000; // 30 seconds
const DEBOUNCE_DELAY = 2000; // 2 seconds debounce for manual changes
const MAX_VERSIONS = 5; // Keep last 5 versions per workflow
const STORAGE_PREFIX = 'workflow_autosave_';

class WorkflowAutosaveService {
  constructor() {
    this.autosaveTimer = null;
    this.debounceTimer = null;
    this.lastSaveTime = null;
    this.isEnabled = true;
  }

  /**
   * Get storage key for a specific workflow
   */
  getStorageKey(workflowId, projectId = 'default') {
    return `${STORAGE_PREFIX}${projectId}_${workflowId}`;
  }

  /**
   * Get versions storage key for a specific workflow
   */
  getVersionsKey(workflowId, projectId = 'default') {
    return `${STORAGE_PREFIX}${projectId}_${workflowId}_versions`;
  }

  /**
   * Save workflow state to localStorage
   */
  saveWorkflow(workflowId, nodes, connections, metadata = {}, projectId = 'default') {
    if (!this.isEnabled) return;

    try {
      const workflowState = {
        workflowId,
        nodes,
        connections,
        metadata: {
          ...metadata,
          savedAt: new Date()?.toISOString(),
          nodeCount: nodes?.length || 0,
          connectionCount: connections?.length || 0
        },
        version: Date.now()
      };

      // Save current state
      localStorage.setItem(
        this.getStorageKey(workflowId, projectId),
        JSON.stringify(workflowState)
      );

      // Save to versions history
      this.saveVersion(workflowId, workflowState, projectId);

      this.lastSaveTime = Date.now();
      
      return {
        success: true,
        savedAt: workflowState?.metadata?.savedAt,
        version: workflowState?.version
      };
    } catch (error) {
      console.error('Error saving workflow:', error);
      
      // Check if quota exceeded
      if (error?.name === 'QuotaExceededError') {
        this.cleanupOldVersions(workflowId, projectId);
        // Retry once after cleanup
        try {
          localStorage.setItem(
            this.getStorageKey(workflowId, projectId),
            JSON.stringify({ workflowId, nodes, connections, metadata })
          );
          return { success: true, warning: 'Storage cleaned up' };
        } catch (retryError) {
          return { success: false, error: 'Storage quota exceeded' };
        }
      }
      
      return { success: false, error: error?.message };
    }
  }

  /**
   * Save a version to history
   */
  saveVersion(workflowId, workflowState, projectId = 'default') {
    try {
      const versionsKey = this.getVersionsKey(workflowId, projectId);
      let versions = [];
      
      const existingVersions = localStorage.getItem(versionsKey);
      if (existingVersions) {
        versions = JSON.parse(existingVersions);
      }

      // Add new version
      versions?.push({
        version: workflowState?.version,
        savedAt: workflowState?.metadata?.savedAt,
        nodeCount: workflowState?.metadata?.nodeCount,
        connectionCount: workflowState?.metadata?.connectionCount
      });

      // Keep only last MAX_VERSIONS
      if (versions?.length > MAX_VERSIONS) {
        versions = versions?.slice(-MAX_VERSIONS);
      }

      localStorage.setItem(versionsKey, JSON.stringify(versions));
    } catch (error) {
      console.error('Error saving version history:', error);
    }
  }

  /**
   * Load workflow state from localStorage
   */
  loadWorkflow(workflowId, projectId = 'default') {
    try {
      const saved = localStorage.getItem(this.getStorageKey(workflowId, projectId));
      
      if (!saved) {
        return { success: false, data: null };
      }

      const workflowState = JSON.parse(saved);
      
      return {
        success: true,
        data: {
          nodes: workflowState?.nodes || [],
          connections: workflowState?.connections || [],
          metadata: workflowState?.metadata || {},
          version: workflowState?.version
        }
      };
    } catch (error) {
      console.error('Error loading workflow:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Get version history for a workflow
   */
  getVersionHistory(workflowId, projectId = 'default') {
    try {
      let versions = localStorage.getItem(this.getVersionsKey(workflowId, projectId));
      return versions ? JSON.parse(versions) : [];
    } catch (error) {
      console.error('Error loading version history:', error);
      return [];
    }
  }

  /**
   * Delete workflow autosave
   */
  deleteWorkflow(workflowId, projectId = 'default') {
    try {
      localStorage.removeItem(this.getStorageKey(workflowId, projectId));
      localStorage.removeItem(this.getVersionsKey(workflowId, projectId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting workflow:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Cleanup old versions to free up space
   */
  cleanupOldVersions(workflowId, projectId = 'default') {
    try {
      const versionsKey = this.getVersionsKey(workflowId, projectId);
      let versions = localStorage.getItem(versionsKey);
      
      if (versions) {
        const parsed = JSON.parse(versions);
        // Keep only last 2 versions when cleaning up
        const cleaned = parsed?.slice(-2);
        localStorage.setItem(versionsKey, JSON.stringify(cleaned));
      }
    } catch (error) {
      console.error('Error cleaning up versions:', error);
    }
  }

  /**
   * Start autosave timer
   */
  startAutosave(workflowId, getStateCallback, onSaveCallback, projectId = 'default') {
    this.stopAutosave(); // Clear any existing timer

    this.autosaveTimer = setInterval(() => {
      const { nodes, connections, metadata } = getStateCallback();
      
      // Only save if there are nodes or connections
      if (nodes?.length > 0 || connections?.length > 0) {
        const result = this.saveWorkflow(workflowId, nodes, connections, metadata, projectId);
        
        if (onSaveCallback) {
          onSaveCallback(result);
        }
      }
    }, AUTOSAVE_INTERVAL);
  }

  /**
   * Stop autosave timer
   */
  stopAutosave() {
    if (this.autosaveTimer) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  /**
   * Debounced save for immediate user actions
   */
  debouncedSave(workflowId, nodes, connections, metadata = {}, projectId = 'default') {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.saveWorkflow(workflowId, nodes, connections, metadata, projectId);
    }, DEBOUNCE_DELAY);
  }

  /**
   * Force immediate save (bypasses debounce)
   */
  forceSave(workflowId, nodes, connections, metadata = {}, projectId = 'default') {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    return this.saveWorkflow(workflowId, nodes, connections, metadata, projectId);
  }

  /**
   * Enable/disable autosave
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    if (!enabled) {
      this.stopAutosave();
    }
  }

  /**
   * Get last save time
   */
  getLastSaveTime() {
    return this.lastSaveTime;
  }

  /**
   * Check if workflow has autosave data
   */
  hasAutosave(workflowId, projectId = 'default') {
    return localStorage.getItem(this.getStorageKey(workflowId, projectId)) !== null;
  }

  /**
   * Get all autosaved workflow IDs for a specific project
   */
  getAllAutosavedWorkflows(projectId = 'default') {
    const workflows = [];
    
    try {
      const prefix = `${STORAGE_PREFIX}${projectId}_`;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix) && !key?.includes('_versions')) {
          const workflowId = key?.replace(prefix, '');
          workflows?.push(workflowId);
        }
      }
    } catch (error) {
      console.error('Error getting autosaved workflows:', error);
    }
    
    return workflows;
  }
}

// Export singleton instance
const workflowAutosaveService = new WorkflowAutosaveService();
export default workflowAutosaveService;
